/**
 * Keeps the identity a tab DISPLAYS in agreement with the identity its requests
 * are SENT AS — GH #198 / #207 / #209 (P1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PROBLEM, IN TWO HALVES
 *
 * Tokens live in `localStorage`, shared by every tab on the origin, and
 * `authedFetch` re-reads them on every request. Nothing told a tab that another
 * tab had changed the session.
 *
 * The mild half — sign out in tab A, and tab B keeps rendering the authenticated
 * shell and the user's data. It only corrects on its next request, so an idle
 * tab shows a signed-out user's data indefinitely.
 *
 * The serious half — sign in as B in tab 2, and tab 1 still renders A's shell
 * (name, company, nav, cached lists) while its next request carries **B's
 * token**. B's token is valid, so there is no 401 and nothing self-corrects.
 * Tab 1 can read and WRITE into B's tenant while displaying A's identity: a
 * "create requirement" click in a stale tab lands in the wrong company, with no
 * error anywhere.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A FULL RELOAD RATHER THAN A RE-HYDRATE
 *
 * The stale identity is not confined to auth state. By the time this fires, the
 * tab may hold cached lists, a crypto session key, and component state built
 * from the previous tenant. Re-hydrating auth alone would fix the shell and
 * leave the cached tenant data on screen — the same divergence, one layer down.
 *
 * A reload is the only action that guarantees nothing from the previous identity
 * survives. It is also what `shellSignOut` already does, for the same reason.
 *
 * No reload loop is possible: after the reload the tab hydrates as the new user,
 * so `currentUserId` matches the token's `sub` and the comparison stops firing.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS DOES NOT TRY TO DO
 *
 * "Let me be signed into two accounts in two tabs" is inherent to a
 * shared-origin `localStorage` session. Supporting it needs per-tab
 * `sessionStorage` or a multi-account model — a feature, not this fix. The
 * ticket says so explicitly.
 */

import { useEffect } from "react";
import { TOKEN_KEYS } from "@/lib/api/client";

/**
 * Reads the `sub` claim without verifying the signature.
 *
 * Verification is the server's job and it still does it — this value is only
 * used to decide whether the tab is displaying the wrong person, which is a UI
 * decision. A forged token would fail at the API regardless, and a tab reloading
 * because of one is the correct outcome anyway.
 */
function subjectOf(token: string | null): string | null {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    /* base64url → base64, then pad. atob rejects the url-safe alphabet. */
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { sub?: unknown };
    return typeof claims.sub === "string" ? claims.sub : null;
  } catch {
    /* Malformed token — treat as "cannot tell", not as "different user". Being
       wrong in that direction would reload the tab on garbage input. */
    return null;
  }
}

export interface CrossTabAuthSyncOptions {
  /** The user the tab is currently RENDERING. null when anonymous. */
  currentUserId: string | null;
  /** Tokens were removed in another tab — this session is over. */
  onSessionCleared: () => void;
  /** Tokens now belong to somebody else. The tab is displaying the wrong person. */
  onForeignSession: (newUserId: string) => void;
}

export function useCrossTabAuthSync({
  currentUserId,
  onSessionCleared,
  onForeignSession,
}: CrossTabAuthSyncOptions): void {
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      /* `storage` only fires in OTHER tabs, which is exactly the scope wanted —
         a tab that changed its own session already knows. */
      if (e.key !== TOKEN_KEYS.access && e.key !== TOKEN_KEYS.refresh) return;

      /* Cleared: signed out elsewhere. Covers e.newValue === null and the
         whole-storage clear, where key is null — filtered above, so this is the
         explicit removal case. */
      if (e.newValue === null) {
        onSessionCleared();
        return;
      }

      /* Only the access token carries an identity worth comparing. A refresh
         token rotating is normal and must not disturb the tab. */
      if (e.key !== TOKEN_KEYS.access) return;

      const nextUserId = subjectOf(e.newValue);
      if (!nextUserId) return;                 // cannot tell — leave it alone
      if (!currentUserId) return;              // anonymous tab; nothing to diverge
      if (nextUserId === currentUserId) return; // same person, token just refreshed

      onForeignSession(nextUserId);
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [currentUserId, onSessionCleared, onForeignSession]);
}
